import { computed, ref, watch } from '@nuxtjs/composition-api';
import { useMutation, useQuery } from 'villus';
import { OrderRateCriteriaDocument, RateOrderDocument, RateOrderMutationVariables } from '~/graphql/orders';
import {
  CreateProductReviewDocument,
  CreateProductReviewMutationVariables,
  ProductRatingMetadataDocument,
  ProductReviewsDocument,
  ProductReviewsQuery,
  ProductReviewsQueryVariables,
} from '~/graphql/Review';
import { MaybeReactive, Unpacked } from '~/types/utils';
import { toNonNullable } from '~/utils/collections';

export function useRatingCategories() {
  const { data, isFetching } = useQuery({
    query: ProductRatingMetadataDocument,
  });

  const ratingCategories = computed(() => {
    return toNonNullable(data.value?.productReviewRatingsMetadata.items);
  });

  return {
    ratingCategories,
    isFetching,
  };
}

export function useOrderRatingCategories() {
  const { data, isFetching } = useQuery({
    query: OrderRateCriteriaDocument,
  });

  const ratingCategories = computed(() => {
    return toNonNullable(data.value?.criteria)?.map(criteria => ({ id: criteria.criteria_id, name: criteria.name }));
  });

  return {
    ratingCategories,
    isFetching,
  };
}

export function useCreateProductReview() {
  const { execute } = useMutation(CreateProductReviewDocument);

  async function createProductReview(input: CreateProductReviewMutationVariables['input']) {
    const { data, error } = await execute({
      input,
    });

    if (error) {
      throw error;
    }

    return data;
  }

  return {
    createProductReview,
  };
}

export function useCreateOrderReview() {
  const { execute, isFetching } = useMutation(RateOrderDocument);

  async function createOrderReview(input: RateOrderMutationVariables['input']) {
    const { data, error } = await execute({
      input,
    });

    if (error) {
      throw error;
    }

    return data;
  }

  return {
    createOrderReview,
    isFetching,
  };
}

export function useProductReviews(variables: MaybeReactive<ProductReviewsQueryVariables>) {
  const { data, error, isFetching } = useQuery({
    query: ProductReviewsDocument,
    variables,
  });

  const reviews = ref<ReturnType<typeof mapReviewNode>[]>([]);
  watch(data, value => {
    const connection = value?.products?.items?.[0]?.reviews;
    if (!connection) {
      return;
    }

    const fetchedPage = connection.page_info?.current_page || 1;
    if (fetchedPage === 1) {
      reviews.value = toNonNullable(connection.items).map(mapReviewNode);
      return;
    }

    reviews.value.push(...toNonNullable(connection.items).map(mapReviewNode));
  });

  return {
    reviews,
    data,
    error,
    isFetching,
  };
}

type ReviewNode = Unpacked<
  NonNullable<Unpacked<NonNullable<ProductReviewsQuery['products']>['items']>>['reviews']['items']
>;

function mapReviewNode(node: ReviewNode) {
  return {
    ...node,
    createdAt: new Date(node?.createdAt || 0),
  };
}
